import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';

const galleryImages = [
  {
    src: 'https://images.unsplash.com/photo-1487530811176-3780de880c2d?w=1200&q=80',
    alt: 'Bouquet de roses élégant',
    category: 'Bouquets',
  },
  {
    src: 'https://images.unsplash.com/photo-1561181286-d3fee7d55364?w=1200&q=80',
    alt: 'Composition florale printanière',
    category: 'Compositions',
  },
  {
    src: 'https://images.unsplash.com/photo-1508610048659-a06b669e3321?w=1200&q=80',
    alt: 'Bouquet champêtre',
    category: 'Bouquets',
  },
  {
    src: 'https://images.unsplash.com/photo-1519225421980-715cb0215aed?w=1200&q=80',
    alt: 'Décoration de mariage',
    category: 'Mariages',
  },
  {
    src: 'https://images.unsplash.com/photo-1490750967868-88aa4486c946?w=1200&q=80',
    alt: 'Arrangement floral pastel',
    category: 'Compositions',
  },
  {
    src: 'https://images.unsplash.com/photo-1469371670807-013ccf25f16a?w=1200&q=80',
    alt: 'Décoration événement',
    category: 'Événements',
  },
  {
    src: 'https://images.unsplash.com/photo-1455659817273-f96807779a8a?w=1200&q=80',
    alt: 'Bouquet romantique',
    category: 'Bouquets',
  },
  {
    src: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=1200&q=80',
    alt: 'Notre boutique',
    category: 'Boutique',
  },
  {
    src: 'https://images.unsplash.com/photo-1563241527-3004b7be0ffd?w=1200&q=80',
    alt: 'Composition moderne',
    category: 'Compositions',
  },
  {
    src: 'https://images.unsplash.com/photo-1522057384400-681b421cfebc?w=1200&q=80',
    alt: 'Centre de table mariage',
    category: 'Mariages',
  },
  {
    src: 'https://images.unsplash.com/photo-1591886960571-74d43a9d4166?w=1200&q=80',
    alt: 'Bouquet de pivoines',
    category: 'Bouquets',
  },
  {
    src: 'https://images.unsplash.com/photo-1518709766631-a6a7f45921c3?w=1200&q=80',
    alt: 'Décoration florale',
    category: 'Événements',
  },
];

const categories = ['Tous', 'Bouquets', 'Compositions', 'Mariages', 'Événements', 'Boutique'];

export default function GalleryPage() {
  const [activeCategory, setActiveCategory] = useState('Tous');
  const [selectedImage, setSelectedImage] = useState(null);

  const filteredImages = activeCategory === 'Tous'
    ? galleryImages
    : galleryImages.filter(img => img.category === activeCategory);

  return (
    <div data-testid="gallery-page" className="pt-24">
      {/* Hero Section */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center max-w-3xl mx-auto">
            <motion.span
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="inline-block text-accent font-accent italic text-lg mb-4"
            >
              Nos créations
            </motion.span>
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="font-serif text-4xl md:text-5xl lg:text-6xl font-semibold text-primary tracking-tight"
            >
              Galerie
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="mt-6 text-lg text-muted-foreground"
            >
              Découvrez notre univers floral à travers nos réalisations. Chaque création est unique
              et témoigne de notre passion pour l'art floral.
            </motion.p>
          </div>
        </div>
      </section>

      {/* Filter Categories */}
      <section className="pb-8">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="flex flex-wrap justify-center gap-3"
          >
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setActiveCategory(category)}
                data-testid={`filter-${category.toLowerCase()}`}
                className={`px-6 py-2 rounded-full text-sm font-medium transition-all ${
                  activeCategory === category
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-secondary text-muted-foreground hover:bg-secondary/80'
                }`}
              >
                {category}
              </button>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="py-8 pb-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            layout
            className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6"
          >
            <AnimatePresence mode="popLayout">
              {filteredImages.map((image, index) => (
                <motion.div
                  key={image.src}
                  layout
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                  transition={{ duration: 0.4, delay: index * 0.05 }}
                  data-testid={`gallery-item-${index}`}
                  onClick={() => setSelectedImage(image)}
                  className="group relative aspect-square rounded-2xl overflow-hidden cursor-pointer"
                >
                  <img
                    src={image.src}
                    alt={image.alt}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  <div className="absolute bottom-0 left-0 right-0 p-4 translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                    <span className="text-white text-sm font-medium">{image.alt}</span>
                    <span className="block text-white/70 text-xs mt-1">{image.category}</span>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </motion.div>
        </div>
      </section>

      {/* Lightbox */}
      <AnimatePresence>
        {selectedImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelectedImage(null)}
            className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4 md:p-8"
            data-testid="gallery-lightbox"
          >
            <button
              onClick={() => setSelectedImage(null)}
              className="absolute top-6 right-6 w-12 h-12 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors"
              data-testid="lightbox-close"
            >
              <X className="w-6 h-6 text-white" />
            </button>
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="relative max-w-5xl max-h-[90vh]"
            >
              <img
                src={selectedImage.src}
                alt={selectedImage.alt}
                className="max-w-full max-h-[85vh] object-contain rounded-lg"
              />
              <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/80 to-transparent rounded-b-lg">
                <h3 className="text-white text-xl font-medium">{selectedImage.alt}</h3>
                <span className="text-white/70 text-sm">{selectedImage.category}</span>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export { GalleryPage };
